'use client';

import React, { useState } from 'react';
import { VisualDiagramComponent } from '../types/content-components.types.js';
import { Image as ImageIcon } from 'lucide-react';

interface VisualDiagramProps {
  component: VisualDiagramComponent;
}

export const VisualDiagram: React.FC<VisualDiagramProps> = ({ component }) => {
  const { props } = component;
  const [imageError, setImageError] = useState(false);

  // Placeholder for image generation
  if (!props.imageUrl) {
    return (
      <div className="w-full">
        {props.title && (
          <h3 className="text-xl font-bold text-gray-900 mb-4">{props.title}</h3>
        )}
        <div className="bg-learn-coral-50 border-2 border-dashed border-learn-coral-300 rounded-xl p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-learn-coral-200 rounded-xl flex items-center justify-center mx-auto mb-4">
              <ImageIcon className="w-8 h-8 text-learn-coral-600" />
            </div>
            <h4 className="text-lg font-semibold text-learn-coral-900 mb-3">
              🎨 Diagram Image Needed
            </h4>
            <p className="text-sm text-learn-coral-800 mb-4 max-w-lg mx-auto">
              Generate a diagram using this prompt:
            </p>
            <div className="bg-white rounded-lg p-4 border border-learn-coral-200 max-w-2xl mx-auto">
              <p className="text-sm text-gray-700 font-mono leading-relaxed">
                {props.imagePrompt || props.alt}
              </p>
            </div>
            {props.annotations && props.annotations.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-learn-coral-700 font-medium mb-2">
                  Annotations to include ({props.annotations.length}):
                </p>
                <div className="bg-white rounded-lg p-3 border border-learn-coral-200 max-w-2xl mx-auto text-left">
                  {props.annotations.map((ann, idx) => (
                    <div key={ann.id} className="text-xs text-gray-700 mb-1">
                      • {ann.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {props.title && (
        <h3 className="text-xl font-bold text-gray-900 mb-4">{props.title}</h3>
      )}
      
      <div className="relative">
        <img
          src={props.imageUrl}
          alt={props.alt}
          onError={() => setImageError(true)}
          className="w-full h-auto rounded-xl shadow-md"
          loading="lazy"
        />
        
        {/* Annotations - positioned absolutely over image */}
        {props.annotations && props.annotations.map((annotation, index) => (
          <div
            key={annotation.id}
            className="absolute group"
            style={{
              left: `${annotation.position.x}%`,
              top: `${annotation.position.y}%`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            {/* Annotation Dot */}
            <div className="w-8 h-8 bg-learn-coral-500 border-2 border-white rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-transform">
              <span className="text-white text-sm font-bold">{index + 1}</span>
            </div>
            
            {/* Annotation Label (appears on hover) */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap max-w-xs">
                <p className="font-semibold">{annotation.label}</p>
                {annotation.description && (
                  <p className="text-gray-300 mt-1">{annotation.description}</p>
                )}
              </div>
              {/* Arrow */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          </div>
        ))}
      </div>

      {props.caption && (
        <p className="text-sm text-gray-700 mt-3 leading-relaxed">
          {props.caption}
        </p>
      )}
    </div>
  );
};

